import type { ProjectExecutionStatus } from "./projectExecutionStatus";

/**
 * Why the Danger zone's one action is unavailable, when it is.
 *
 * Reads `ProjectExecutionStatus` rather than re-deriving "connected" from
 * `removalTargets.length === 0`. That count answers "can this session remove a
 * Forge project right now", which is strictly narrower than "does one exist" —
 * and answering the second with the first is what let a genuinely-linked
 * project claim nothing was connected.
 */

export interface CmmdProjectDangerInput {
  readonly executionStatus: ProjectExecutionStatus;
  /** Forge execution projects this session can actually act on. */
  readonly forgeTargetCount: number;
}

/**
 * Why the Forge project cannot be deleted, or `undefined` when it can.
 *
 * Four distinct states, four distinct sentences. `projectExecutionStatus.ts`
 * exists precisely to keep them apart; this is the consumer that has to honour
 * that, because a single "no execution project is connected" is a lie in three
 * of the four.
 */
export function forgeExecutionUnavailableMessage(
  input: CmmdProjectDangerInput,
): string | undefined {
  if (input.forgeTargetCount > 0) return undefined;
  switch (input.executionStatus) {
    case "no_repo":
      return "No repository linked.";
    case "repo_ready":
      return "No Forge project yet.";
    case "error":
      return "Couldn't check the Forge link.";
    case "linked":
      // A Forge project DOES exist. Nothing in this session can reach it.
      return "Its runtime isn't loaded here.";
  }
}
